import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RemakeImagesPanel } from './RemakeImagesPanel'

interface RemakeJury {
  agent_style: string
  title: string
  bullets: string[]
  description: string
  backend_keywords?: { search_terms?: string }
}

interface RemakeResult {
  variants: RemakeJury[]
  jury_index: number
}

export interface RemakeApplyRemakeSidebarProps {
  remakeResult: RemakeResult | null
  remakeId: string | null
}

export function RemakeApplyRemakeSidebar({ remakeResult, remakeId }: RemakeApplyRemakeSidebarProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'text' | 'images'>('text')
  const jury = remakeResult?.variants?.[remakeResult.jury_index] ?? null

  return (
    <aside className="bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 flex flex-col">
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {t('remakeApply.sidebarPlaceholder.title')}
        </div>
      </div>
      <div className="flex border-b border-neutral-200 dark:border-neutral-800" role="tablist">
        <button
          type="button"
          role="tab"
          aria-pressed={tab === 'text'}
          onClick={() => setTab('text')}
          className={`flex-1 py-2 text-sm transition-colors ${
            tab === 'text'
              ? 'font-medium text-neutral-900 dark:text-neutral-100 border-b-2 border-orange-500'
              : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
          }`}
        >
          {t('remakeApply.sidebar.tabText')}
        </button>
        <button
          type="button"
          role="tab"
          aria-pressed={tab === 'images'}
          onClick={() => setTab('images')}
          className={`flex-1 py-2 text-sm transition-colors ${
            tab === 'images'
              ? 'font-medium text-neutral-900 dark:text-neutral-100 border-b-2 border-orange-500'
              : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
          }`}
        >
          {t('remakeApply.sidebar.tabImages')}
        </button>
      </div>

      {tab === 'text' ? (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-xs">
          {jury ? (
            <>
              <SidebarBlock label={t('remakeApply.content.fieldTitle')}>{jury.title}</SidebarBlock>
              <div>
                <div className="text-neutral-500 mb-1">{t('remakeApply.content.fieldBullets')}</div>
                <ol className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 list-decimal list-inside space-y-0.5">
                  {jury.bullets.map((b, i) => (
                    <li key={i} className="truncate">
                      {b}
                    </li>
                  ))}
                </ol>
              </div>
              <SidebarBlock label={t('remakeApply.content.fieldDescription')} clamp>
                {jury.description}
              </SidebarBlock>
              <button
                type="button"
                className="w-full text-xs text-orange-600 hover:text-orange-700 hover:underline py-2"
              >
                {t('remakeApply.sidebar.showMore')} →
              </button>
            </>
          ) : (
            <div className="text-neutral-400 italic">—</div>
          )}
        </div>
      ) : (
        <RemakeImagesPanel remakeId={remakeId} />
      )}

      <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-800 bg-amber-50/50 dark:bg-amber-950/20">
        <div className="text-xs font-semibold text-amber-900 dark:text-amber-200">
          💡 {t('remakeApply.sidebar.tipTitle')}
        </div>
        <div className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
          {t('remakeApply.sidebar.tipBody')}
        </div>
      </div>
    </aside>
  )
}

function SidebarBlock({
  label,
  children,
  clamp,
}: {
  label: string
  children: React.ReactNode
  clamp?: boolean
}) {
  return (
    <div>
      <div className="text-neutral-500 mb-1">{label}</div>
      <div
        className={`bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md px-2 py-1.5 ${
          clamp ? 'line-clamp-3' : ''
        }`}
      >
        {children}
      </div>
    </div>
  )
}
